// @flow
//
// DeepLink component.
//
// This component doesn't need to be connected as it doesn't actually grab anything from the store -
// it uses branch and the parameters passed to it

import React from "react";

import { isIOSDevice } from "../../../utils/browser";
import { requestDeepLink } from "../../../utils/branchSDK";

let deepLinkIndex = 0;

interface Props {
  accountId: string;
  data: any;
  linkId: string;
  deepLink: string;
  distinctId: string;
  campaign: string;
  channel: string;
  feature: string;
  superId: string;
  tags: string[];
  deepLinkClicked: any;
  deepLinkRequested: any;
  deepLinkReceived: any;
  onClick: any;
}

type DeepLinkState = {
  href: string;
};

const getBranchData = (props: any): any => {
  const {
    accountId,
    campaign,
    channel,
    data,
    distinctId,
    feature,
    superId,
    tags,
  } = props;

  const defaultData = { accountId, distinctId, superId };

  // we will assume any provided data should override the defaults
  Object.assign(defaultData, data);

  const branchLink = {
    campaign,
    channel,
    data: defaultData,
    feature,
    tags,
  };

  return branchLink;
};

/**
 * This function exists purely for testing;
 *
 * setState and setProps are incredibly problematic with a connected component.
 */
export default function deepLink(
  WrappedComponent: any,
  deepLinkData?: any
): any {
  /**
   * DeepLink
   *
   * This is a HOC that constructs a deep link component.
   *
   * Right now, we only support iOS.
   *
   * For now, as a result, the component itself handles when it renders by asking if the
   * page is being viewed by an iOS device. In the future, we might actually have situations
   * where we want to deeplink to Android and iOS, but not Windows Phone, or deeplink just
   * to Android, so when that day transpires we will have to revisit this and probably construct it
   * such that we can elect platforms via props.
   *
   * I should note that Branch itself can deep link to just about anything, and as far as I know,
   * it uses the same API for all platforms.
   *
   * Tags, campaign, channel, feature, and stage are all optional, and correspond to UTM
   * ( Urchin Tracking Module ) parameters, which Branch (and other analytics platforms) use.
   *
   * Note: the name 'data' was chosen because it corresponds to the Branch API; I would have
   * personally preferred "params" but I think it's better to have consistency.
   *
   * propTypes
   * @property {Object} data An unsealed object with parameters to pass to our deeplinking service.
   * @property {string[]} [tags]  An array of strings, to tag the deeplink. This is used often to identify variables such as election to A/B tests or if a deeplink has more than one entry point.
   * @property {string} [campaign] The campaign for the deeplink. Used as a product name to denote a promotion. `utm_campaign` equivalent.
   * @property {string} [channel] The channel for the deeplink. Often used to identify promotions or campaigns ore feature rollouts. `utm_source` equivalent.
   * @property {string} [feature] The feature for the deeplink. This identifies what created the deeplink (so, email link, facebook link , or the most common, app link). `utm_medium` equivalent.
   */

  const {
    data = {},
    campaign = "",
    channel = "web",
    feature = "",
    tags = undefined,
  } = deepLinkData || {};

  class DeepLink extends React.Component<Props, DeepLinkState> {
    constructor(props: Props) {
      super(props);
      this.state = { href: "" };
    }

    click = (e: Event) => {
      if (this.state.href && this.state.href.length <= 0) {
        e.preventDefault();

        requestDeepLink(this.props.linkId, getBranchData(this.props));
      }

      if (this.props.onClick) {
        this.props.onClick(e);
      }
    };

    componentDidMount = () => {
      requestDeepLink(this.props.linkId, getBranchData(this.props)).then(
        (linkURL: string) => this.updateBranchLink(linkURL)
      );
    };

    componentDidUpdate = (prevProps: any) => {
      if (prevProps.linkId !== this.props.linkId) {
        requestDeepLink(this.props.linkId, getBranchData(this.props)).then(
          this.updateBranchLink
        );
      }
    };

    updateBranchLink = (branchURL: string) => {
      this.setState({ href: branchURL });
    };

    render() {
      // Pull out all of our props (and intercepted onClick) and pass the rest.
      //
      // We alias 'deepLink' to 'href' here.
      const {
        accountId,
        campaign,
        channel,
        data,
        distinctId,
        feature,
        onClick, //intercepted
        superId,
        tags,
        ...props
      } = this.props; //eslint-disable-line no-unused-vars
      const { href } = this.state;

      // Don't display the link until branchSDK is available...
      return isIOSDevice() && href && href.length > 0 ? (
        <WrappedComponent onClick={this.click} href={href} {...props} />
      ) : null;
    }
  }
  return DeepLink;
}
